import { isEmpty, isNull, pick } from 'lodash';
import { TextColumnFilterProps } from '../types';
import { Operator } from '@ardoq/api-types';
import { DEFAULT_OPERATOR_TO_LABEL } from '@ardoq/query-builder';
import { Select, SelectOption } from '@ardoq/select';
import { RemoveFilterButtonInTopRightCorner, SelectContainer } from './atoms';
import { Maybe } from '@ardoq/common-helpers';
import { ColumnFilterMultiselectWithLoadSuggestions } from './ColumnFilterMultiselectWithLoadSuggestions';
import { TEXT_FILTER_OPERATORS } from './filterUtils';
import { isEmptyOptionSelected } from '../utils';

const TEXT_OPERATOR_SELECT_OPTIONS = Object.entries(
  pick(DEFAULT_OPERATOR_TO_LABEL, TEXT_FILTER_OPERATORS)
).map(([operator, label]) => ({
  label,
  value: operator,
})) as SelectOption<Operator>[];

// type column is a special text column which is never empty
const TYPE_OPERATOR_SELECT_OPTIONS = TEXT_OPERATOR_SELECT_OPTIONS.filter(
  option => ![Operator.IS_EMPTY, Operator.IS_NOT_EMPTY].includes(option.value)
);

const TextColumnFilter = ({
  column,
  selected,
  loadSuggestions,
  menuTarget,
  isReadOnly,
  removeFilter,
  changeFilterExpression,
}: TextColumnFilterProps & {
  menuTarget: Maybe<HTMLElement>;
}) => {
  const selectedOperator = selected[0]?.operator || Operator.EQUAL;
  return (
    <SelectContainer>
      {!isReadOnly && (
        <RemoveFilterButtonInTopRightCorner
          onClick={() => removeFilter(column.key)}
        />
      )}
      <Select
        label={column.label}
        options={
          column.key === 'type'
            ? TYPE_OPERATOR_SELECT_OPTIONS
            : TEXT_OPERATOR_SELECT_OPTIONS
        }
        value={selectedOperator}
        onChange={operatorOption => {
          if (operatorOption) {
            changeFilterExpression(
              column,
              isEmptyOptionSelected(operatorOption.value) || isEmpty(selected)
                ? [{ value: null, operator: operatorOption.value }]
                : selected.map(expression => ({
                    value: expression.value,
                    operator: operatorOption.value,
                  }))
            );
          }
        }}
        menuPortalTarget={menuTarget}
        isReadOnly={isReadOnly}
      />

      {!isEmptyOptionSelected(selectedOperator) && (
        <ColumnFilterMultiselectWithLoadSuggestions
          withCloseButton={false}
          menuTarget={menuTarget}
          column={column}
          selected={selected.filter(expression => !isNull(expression.value))}
          allowFreeText={true}
          showLabel={false}
          onValueChange={values => {
            return !isNull(values) && !isEmpty(values)
              ? changeFilterExpression(
                  column,
                  values.map(value => ({
                    ...value,
                    operator: selectedOperator,
                  }))
                )
              : changeFilterExpression(column, [
                  { value: null, operator: selectedOperator },
                ]); // not reset currently selected operator even if values are cleared
          }}
          loadSuggestions={loadSuggestions}
          removeFilter={removeFilter}
          isReadOnly={isReadOnly}
        />
      )}
    </SelectContainer>
  );
};

export { TextColumnFilter };
